import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as LLMVendorModelsAPI from 'api/LLMVendorModels';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';
import { PAGES } from 'sections/LLMVendorModels/constants';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  selectedPage: PAGES.MAIN_PAGE,
  ttsvendor: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Description', key: 'description', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Website', key: 'website', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Help Text', key: 'help_text', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  ttsvendorModels: {
    selectedVendor: null,
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Model_Key', key: 'model_key', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getTTSVendorData = createAsyncThunk('ttsVendorModels/getTTSVendorData', async ({ params, signal }) => {
  const res = await LLMVendorModelsAPI.getTTSVendorData(params, signal);
  return res?.data?.data;
});

const createTTSVendor = createAsyncThunk('ttsVendorModels/createTTSVendor', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.createTTSVendor(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateTTSVendor = createAsyncThunk('ttsVendorModels/updateTTSVendor', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.updateTTSVendor(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteTTSVendor = createAsyncThunk('ttsVendorModels/deleteTTSVendor', async (id) => {
  try {
    const res = await LLMVendorModelsAPI.deleteTTSVendor(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

// Super-admin DRF list responses are unwrapped — reducers below
// consume ``action.payload.count`` / ``action.payload.results``.
const getTTSVendorModelsData = createAsyncThunk('ttsVendorModels/getTTSVendorModelsData', async ({ params, signal }) => {
  const res = await LLMVendorModelsAPI.getTTSVendorModelsData(params, signal);
  return res?.data;
});

const createTTSVendorModels = createAsyncThunk('ttsVendorModels/createTTSVendorModels', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.createTTSVendorModels(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateTTSVendorModels = createAsyncThunk('ttsVendorModels/updateTTSVendorModels', async (params) => {
  try {
    const res = await LLMVendorModelsAPI.updateTTSVendorModels(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteTTSVendorModels = createAsyncThunk('ttsVendorModels/deleteTTSVendorModels', async (id) => {
  try {
    const res = await LLMVendorModelsAPI.deleteTTSVendorModels(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const TTSVendorModelsSlice = createSlice({
  name: 'ttsVendorModels',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    //Vendor Tab
    setVendorCurrentPage: (state, action) => {
      state.ttsvendor.pagination.currentPage = action.payload;
    },
    setVendorPageSize: (state, action) => {
      state.ttsvendor.pagination.pageSize = action.payload;
    },
    setSelectedVendorData: (state, action) => {
      state.ttsvendorModels.selectedVendor = action.payload;
    },

    //Action Point Tab
    setVendorModelsCurrentPage: (state, action) => {
      state.ttsvendorModels.pagination.currentPage = action.payload;
    },
    setVendorModelsPageSize: (state, action) => {
      state.ttsvendorModels.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getTTSVendorData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getTTSVendorData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.ttsvendor.pagination.totalRecords = action.payload?.count;
      state.ttsvendor.data = action.payload?.results || [];
    },
    [getTTSVendorData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createTTSVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createTTSVendor.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createTTSVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateTTSVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateTTSVendor.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.ttsvendor.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.ttsvendor.data = updatedData;
    },

    [updateTTSVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteTTSVendor.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteTTSVendor.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteTTSVendor.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [getTTSVendorModelsData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getTTSVendorModelsData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.ttsvendorModels.pagination.totalRecords = action.payload?.count;
      state.ttsvendorModels.data = action.payload?.results || [];
    },
    [getTTSVendorModelsData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createTTSVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createTTSVendorModels.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createTTSVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateTTSVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateTTSVendorModels.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.ttsvendorModels.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.ttsvendorModels.data = updatedData;
    },

    [updateTTSVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteTTSVendorModels.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteTTSVendorModels.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteTTSVendorModels.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const {
  resetState,
  setSelectedPage,
  setVendorCurrentPage,
  setVendorPageSize,
  setSelectedVendorData,
  setVendorModelsCurrentPage,
  setVendorModelsPageSize
} = TTSVendorModelsSlice.actions;

export default TTSVendorModelsSlice.reducer;

export {
  resetState,
  setSelectedPage,
  //Vendor
  setVendorCurrentPage,
  setVendorPageSize,
  setSelectedVendorData,
  getTTSVendorData,
  createTTSVendor,
  updateTTSVendor,
  deleteTTSVendor,
  //Vendor Models
  setVendorModelsCurrentPage,
  setVendorModelsPageSize,
  getTTSVendorModelsData,
  createTTSVendorModels,
  updateTTSVendorModels,
  deleteTTSVendorModels
};
